import { adminHttp } from '@network';
import { Button, Input, Modal } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import React, { Dispatch, useEffect, useRef, useState } from 'react';

import { Credit } from '@types';
import { dateFormatter, showAxiosError } from '@utils';
import IconButton from '@mui/material/IconButton';
import { BlueButton, BlueOutlineButton, ConfirmDelete, InitialCard, Scroll } from '@components';
import cls from './CreditComments.module.css';


interface CommentProps {
  item: any;
  onEdit: Dispatch<any>;
  onDelete: (id: number) => void;
  editable: boolean;
  loading: boolean;
}

const Comment = ({ item, onEdit, onDelete, editable, loading }: CommentProps) => (
  <div className={cls.comment}>
    <div className={cls.commentText}>
      <div className={cls.commentContent}>{item.comment}</div>
      {editable ? <div className={cls.commentActions}>
        <IconButton className={cls.editBtn} onClick={() => onEdit(item)} disabled={loading}>
          <EditIcon />
        </IconButton>
        <ConfirmDelete
          title="Do you really want to delete the comment?"
          cb={() => onDelete(item.id)}
          disabled={loading}
        >
          <IconButton className={cls.deleteBtn} disabled={loading}>
            <DeleteIcon />
          </IconButton>
        </ConfirmDelete>
      </div> : null}
    </div>
    <div className={cls.commentInfo}>
      <div className={cls.commentName}>{item.adminName}</div>
      <div className={cls.commentDate}>{dateFormatter.toDateTime(item.createdAt)}</div>
    </div>
  </div>
);

export const CreditComments = ({ credit, editable, onUpdated }: {
  credit: Credit;
  editable: boolean;
  onUpdated: () => Promise<void>;
}) => {
  const [comment, setComment] = useState<any | null>();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const textRef = useRef<any>();

  const onSubmit = async () => {
    if (!text.trim().length && !comment.comment.trim().length) return;
    setLoading(true);
    const isNew = !comment || !comment.id;
    try {
      await adminHttp.addComment({
        id: isNew ? undefined : comment.id,
        comment: isNew ? text.trim() : comment.comment.trim(),
        partnerClass: 'Credit',
        partnerId: credit.id,
        showToPartner: false,
      });
      await onUpdated();
      hideModal();
    } catch (e: any) {
      showAxiosError(e);
    } finally {
      setLoading(false);
    }
  };

  const onCommentDelete = async (id: number) => {
    setLoading(true);
    try {
      await adminHttp.removeComment(id);
      await onUpdated();
    } catch (e: any) {
      showAxiosError(e);
    } finally {
      setLoading(false);
    }
  };

  const hideModal = () => {
    setComment(null);
    setText('');
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (comment) {
      setText('');
      setIsModalVisible(true);
    }
  }, [comment]);

  return (
    <InitialCard autoHeight extraPadding left="Comments">
      <div className={cls.wrap}>
        <div className={cls.list}>
          <Scroll height={200}>
            {credit.comments.map((c: any) => (
              <Comment
                key={c.id}
                loading={loading}
                item={c}
                editable={editable}
                onEdit={setComment}
                onDelete={onCommentDelete} />
            ))}
          </Scroll>
        </div>
        {editable ? <div className={cls.listFooter}>
          <Button
            type="primary"
            className={cls.plusBtn}
            onClick={() => setIsModalVisible(true)}
          >
            <PlusCircleOutlined /> Admin Comments
          </Button>
        </div> : null}
      </div>

      <Modal
        open={isModalVisible}
        title={comment ? 'Edit comment' : 'Add comment'}
        okText={comment ? 'Update' : 'Save'}
        onOk={() => {}}
        confirmLoading={loading}
        onCancel={hideModal}
        wrapClassName={cls.modal}
        centered
        footer={(
          <div className={cls.footer}>
            <BlueOutlineButton onClick={hideModal} size="small">Cancel</BlueOutlineButton>
            <BlueButton
              className={cls.saveBtn}
              disabled={loading}
              onClick={onSubmit}>Save</BlueButton>
          </div>
        )}
      >
        <Input.TextArea
          className={cls.commentField}
          ref={textRef}
          value={comment ? comment.comment : text}
          showCount
          maxLength={400}
          rows={5}
          onChange={e => {
            if (comment) {
              setComment({ ...comment, comment: e.target.value });
            } else {
              setText(e.target.value);
            }
          }}
          autoFocus
        />
      </Modal>
    </InitialCard>
  );
};

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M9 0C4.03021 0 0 4.03021 0 9C0 13.9698 4.03021 18 9 18C13.9698 18 18 13.9698 18 9C17.992 4.03021 13.9658 0.00399822 9 0Z"
      style={{ fill: 'var(--primary-color)' }} />
    <path d="M12.3863 5.60962C12.2303 5.45369 11.9785 5.45369 11.8225 5.60962L8.99978 8.43237L6.17704 5.60962C6.0211 5.45369 5.76922 5.45369 5.61329 5.60962C5.45736 5.76555 5.45736 6.01744 5.61329 6.17337L8.43603 8.99612L5.61329 11.8189C5.45736 11.9748 5.45736 12.2267 5.61329 12.3826C5.76922 12.5385 6.0211 12.5385 6.17704 12.3826L8.99978 9.55987L11.8225 12.3826C11.9785 12.5385 12.2303 12.5385 12.3863 12.3826C12.5422 12.2267 12.5422 11.9748 12.3863 11.8189L9.56353 8.99612L12.3863 6.17337C12.5422 6.01744 12.5422 5.76555 12.3863 5.60962Z"
      fill="white" />
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 0C4.03021 0 0 4.03021 0 9C0 13.9698 4.03021 18 9 18C13.9698 18 18 13.9698 18 9C17.992 4.03021 13.9658 0.00399822 9 0Z"
      style={{ fill: 'var(--primary-color)' }} />
    <path d="M9.99013 4.87655L12.4434 6.4261L7.86212 13.6792L5.40886 12.1297L9.99013 4.87655Z"
      fill="white" />
    <path d="M11.5172 2.45885C11.9389 1.79122 12.8299 1.59688 13.5074 2.02477C14.1848 2.45267 14.3922 3.34077 13.9705 4.0084L13.2069 5.21725L10.7537 3.6677L11.5172 2.45885Z"
      fill="white" />
    <path d="M4.72663 15.9266L7.48035 14.2836L5.02709 12.7341L4.72663 15.9266Z" fill="white" />
  </svg>
);
